import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Card, EmptyState, IconButton, Menu, PageHeader, Pill, useConfirm, useToast } from '../ui';
import { ChevronDownIcon, DownloadIcon, EditIcon, LocationIcon, MoreIcon, PlusIcon, TrashIcon, UploadIcon } from '../ui/icons';
import api from '../config/api';
import { sectionCrumb } from '../settings/SectionLayout';
import LocationTree from './LocationTree';
import { showImportResult } from './importResult';

function LocationTypeList({ locationTypes, onDelete, onCreateNew, onEdit, onRefresh, isAdmin }) {
  const { t } = useTranslation();
  const toast = useToast();
  const confirm = useConfirm();
  const fileInputRef = useRef(null);
  // Types whose places are shown; a single type opens by itself, once.
  const [openTypes, setOpenTypes] = useState(() => new Set());
  const autoOpened = useRef(false);
  useEffect(() => {
    if (!autoOpened.current && locationTypes.length === 1) {
      autoOpened.current = true;
      setOpenTypes(new Set([locationTypes[0].id]));
    }
  }, [locationTypes]);
  const toggleType = (id) => setOpenTypes(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const handleExport = async () => {
    try {
      const response = await api.get('/location-types/export', {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `location_types_export_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting location types:', error);
      toast.error(t('locationTypes.exportError'));
    }
  };

  const handleImport = () => {
    fileInputRef.current.click();
  };

  const handleFileSelect = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      const text = await file.text();
      const importData = JSON.parse(text);
      
      if (importData.type !== 'location_types') {
        toast.error(t('locationTypes.invalidFile'));
        return;
      }

      const response = await api.post('/location-types/import', importData);
      
      if (response.data.success) {
        showImportResult(toast, t, response.data);
        if (onRefresh) onRefresh();
      } else {
        toast.error(t('settings.importFailed', { error: response.data.error || t('common.unknownError') }));
      }
    } catch (error) {
      console.error('Error importing location types:', error);
      toast.error(t('locationTypes.importError', { error: error.message }));
    }
    
    // Reset file input
    event.target.value = '';
  };

  const askDelete = async (locationType) => {
    if (await confirm({
      title: t('locationTypes.confirmDeleteTitle'),
      message: t('locationTypes.confirmDelete', { name: locationType.name }),
      confirmLabel: t('locationTypes.deleteType'),
      danger: true,
    })) onDelete(locationType.id);
  };

  return (
    <div>
      <PageHeader
        breadcrumb={[sectionCrumb(t, 'settings'), { label: t('locationTypes.title') }]}
        title={t('locationTypes.title')}
        subtitle={t('locationTypes.intro')}
        actions={(
          <>
            <Button variant="secondary" icon={DownloadIcon} onClick={handleExport}>{t('settings.export')}</Button>
            <Button variant="secondary" icon={UploadIcon} onClick={handleImport}>{t('settings.import')}</Button>
            <Button icon={PlusIcon} onClick={onCreateNew}>{t('locationTypes.create')}</Button>
          </>
        )}
      />

      <input type="file" ref={fileInputRef} onChange={handleFileSelect} accept=".json" hidden />

      {locationTypes.length === 0 ? (
        <EmptyState icon={LocationIcon} title={t('locationTypes.emptyTitle')}
                    action={<Button icon={PlusIcon} onClick={onCreateNew}>{t('locationTypes.create')}</Button>}>
          {t('locationTypes.empty')}
        </EmptyState>
      ) : (
        <ul className="place-types">
          {locationTypes.map(locationType => {
            const open = openTypes.has(locationType.id);
            const titleId = `location-type-${locationType.id}`;
            const panelId = `location-type-${locationType.id}-places`;
            return (
              <li key={locationType.id}>
                <Card as="section" aria-labelledby={titleId} className="place-type">
                  <div className="place-type__head">
                    <div className="place-type__who">
                      <h2 id={titleId} className="place-type__title">{locationType.name}</h2>
                      <p className="place-type__meta">
                        {t('collections.locationCount', { count: locationType.location_count ?? 0 })}
                        {!locationType.is_mine && <Pill>{t('settings.shared')}</Pill>}
                      </p>
                    </div>
                    <div className="place-type__actions">
                      <Button variant="secondary" size="sm" iconRight={ChevronDownIcon} onClick={() => toggleType(locationType.id)}
                              aria-expanded={open} aria-controls={open ? panelId : undefined} className={`place-type__toggle${open ? ' is-open' : ''}`}>
                        {open ? t('locations.hidePlaces') : t('locations.showPlaces')}
                      </Button>
                      {locationType.is_mine && (
                        <Menu
                          label={t('locationTypes.actionsFor', { name: locationType.name })}
                          align="end"
                          items={[
                            { id: 'rename', label: t('locations.renameType'), icon: EditIcon, onSelect: () => onEdit(locationType) },
                            'separator',
                            { id: 'delete', label: t('common.delete'), icon: TrashIcon, danger: true, onSelect: () => askDelete(locationType) },
                          ]}
                          trigger={(props) => (
                            <IconButton {...props} icon={MoreIcon} variant="ghost" label={t('locationTypes.actionsFor', { name: locationType.name })} />
                          )}
                        />
                      )}
                    </div>
                  </div>
                  {open && (
                    <div id={panelId} className="place-type__tree">
                      <LocationTree locationType={locationType} canManage={locationType.is_mine || isAdmin} onChanged={onRefresh} />
                    </div>
                  )}
                </Card>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

export default LocationTypeList;
